import React, { useState, useEffect, useCallback } from "react";
import { Users } from "lucide-react";
import { EditCustomerPackageModal } from "../../components/sunbeds/EditCustomerPackageModal";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { CustomerPackagesTable, CustomerPackage } from "../../components/sunbeds/CustomerPackagesTable";
import {
  getSunbedCustomerPackages,
  SunbedCustomerPackageApi,
  updateSunbedCustomerPackage,
  ApiError,
} from "../../services/sunbeds";

const CustomerPackages: React.FC = () => {
  const [packages, setPackages] = useState<CustomerPackage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [editingPackage, setEditingPackage] = useState<CustomerPackage | null>(
    null
  );

  const fetchCustomerPackages = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getSunbedCustomerPackages();

      const apiData: SunbedCustomerPackageApi[] = res.data || [];

      // Map API result into the table view model
      const mapped: CustomerPackage[] = apiData.map((pkg) => ({
        id: String(pkg.id),
        customerName: pkg.user?.name || "Unknown",
        packageName: pkg.package_name || pkg.sunbed?.label || "Sunbed Package",
        usedMinutes: pkg.used_minutes ?? 0, // pkg.used_minutes no longer in type, defaulting to 0 or derived
        remainingMinutes: pkg.remaining_balance ?? 0,
      }));

      setPackages(mapped);
    } catch (error) {
      console.error("Failed to load customer packages from /sunbed-bookings", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load from /sunbed-bookings
  useEffect(() => {
    fetchCustomerPackages();
  }, [fetchCustomerPackages]);

  const filteredPackages = packages.filter(
    (p) =>
      p.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.packageName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedPackages = filteredPackages.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleUpdate = async (newMinutes: number) => {
    if (!editingPackage) return;

    try {
      // Extra client-side guard (should already be enforced in the modal)
      if (newMinutes > editingPackage.remainingMinutes) {
        alert(
          `New remaining minutes (${newMinutes}) cannot be greater than the current balance (${editingPackage.remainingMinutes}).`
        );
        return;
      }

      await updateSunbedCustomerPackage(editingPackage.id, newMinutes);

      // Refresh list from server to reflect latest values
      await fetchCustomerPackages();
      setEditingPackage(null);
    } catch (err: any) {
      const apiErr = err as ApiError;
      const message =
        (apiErr as any).message ||
        (apiErr.type === "serverValidation"
          ? "Validation error while updating package."
          : "Failed to update customer package.");
      alert(message);
    }
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        {/* Settings-style Header */}
        <SubPageHeader
          title="Customer Packages"
          subtitle="View and manage customer package usage and remaining balances"
          icon={Users}
        />

        {/* Action Toolbar */}
        <SunbedActionToolbar
          totalItems={packages.length}
          showingStart={paginatedPackages.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredPackages.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search customers..."
        />

        {/* Table Section */}
        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <CustomerPackagesTable
            packages={paginatedPackages}
            indexOfFirstItem={indexOfFirstItem}
            onEdit={setEditingPackage}
            isLoading={isLoading}
          />

          {/* Pagination Footer */}
          <PaginationFooter
            totalItems={packages.length}
            filteredTotal={filteredPackages.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <EditCustomerPackageModal
        isOpen={!!editingPackage}
        onClose={() => setEditingPackage(null)}
        onUpdate={handleUpdate}
        currentMinutes={editingPackage?.remainingMinutes || 0}
      />
    </div>
  );
};
export default CustomerPackages;
